/**
 * resolveEntityName — the actual display NAME of a created entity, derived from
 * its create-action payload properties, or `undefined` when no name property is
 * set.
 *
 * Why this exists: the backend's `enriched_data.object_label` for a CUSTOM
 * entity create is just the entity-type label (e.g. "New Org Bug") — it does not
 * carry the instance's real name. The real name lives in the payload's
 * `ahoy_display_name` property (the canonical name every entity carries). So
 * both the action title and the card must resolve the name from the payload,
 * falling back to object_label only when there's genuinely no name.
 *
 * Lives in lib (not components/proposals/shared) so the lib-side action registry
 * renderers can use it too, without a lib→components import.
 */

import { formatMaybeMultiValue } from '@/lib/crm/utils/formatMultiValue';
import { unwrapPropertyValue } from '@/types/crm';

export function resolveEntityName(
  properties: Record<string, unknown>,
  entityType: string
): string | undefined {
  const getValue = (key: string): unknown =>
    unwrapPropertyValue(properties[key]);
  const asName = (v: unknown): string | undefined =>
    v != null && String(v).trim() !== '' ? String(v) : undefined;

  if (entityType === 'ahoy_contact') {
    // Normalize through asName so whitespace-only first/last names fall back to
    // email instead of rendering a blank or space-padded title.
    const first = asName(getValue('ahoy_first_name'));
    const last = asName(getValue('ahoy_last_name'));
    if (first && last) {
      return `${first} ${last}`;
    }
    if (first) {
      return first;
    }
    if (last) {
      return last;
    }
    const email = getValue('ahoy_email');
    if (email) {
      return formatMaybeMultiValue(email);
    }
    return undefined;
  }

  if (entityType === 'ahoy_company') {
    return asName(getValue('ahoy_name')) ?? asName(getValue('ahoy_domain'));
  }

  if (entityType === 'ahoy_deal') {
    return (
      asName(getValue('ahoy_name')) ?? asName(getValue('ahoy_display_name'))
    );
  }

  if (entityType === 'ahoy_note') {
    return asName(getValue('ahoy_title'));
  }

  // Custom / any other entity type: the canonical display-name property, then
  // generic name/title fallbacks.
  return (
    asName(getValue('ahoy_display_name')) ??
    asName(getValue('ahoy_name')) ??
    asName(getValue('ahoy_title'))
  );
}
